import { mergePageFieldMaps, overlayAuthoritativeFieldKeys } from '@/common/utils/mergePageFieldMaps'

const SERVICE_CLIENT_DATA_FIELD_KEYS = new Set([
  'attributes',
  'clientData',
  'element_states',
  'errors',
  'exception',
  'fieldAttributes',
  'formError',
  'hasFormError',
  'params',
  'shouldPreserveEmptyIncoming',
  'status',
])

function isPlatformTypedFieldShell(obj: Record<string, any>): boolean {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  if (!Object.prototype.hasOwnProperty.call(obj, 'type')) return false
  if (!Object.prototype.hasOwnProperty.call(obj, 'value')) return false
  const allowed = new Set(['type', 'value', '_attributes', '_text'])
  return Object.keys(obj).every((k) => allowed.has(k))
}

export function isServiceClientDataFieldKey(key: string): boolean {
  return SERVICE_CLIENT_DATA_FIELD_KEYS.has(key)
}

export function sanitizeClientDataFieldMap(fields: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [key, value] of Object.entries(fields || {})) {
    if (isServiceClientDataFieldKey(key)) {
      continue
    }
    out[key] = value
  }
  return out
}

/**
 * Собирает плоский map полей из `data.fields` для createSafeClientData.
 */
export function resolveClientDataActualFields(data: any): Record<string, any> {
  const f = data?.fields
  if (!f || typeof f !== 'object' || Array.isArray(f)) {
    return {}
  }

  const out: Record<string, any> = {}
  let cursor: any = f
  let depth = 0
  const maxDepth = 12

  while (cursor && typeof cursor === 'object' && !Array.isArray(cursor) && depth < maxDepth) {
    depth++
    const {
      fields: next,
      fieldAttributes: _fa,
      errors: _er,
      attributes: _at,
      ...siblings
    } = cursor as Record<string, any>

    Object.assign(out, siblings)

    if (next === undefined || next === null) {
      break
    }

    if (typeof next === 'object' && !Array.isArray(next) && isPlatformTypedFieldShell(next)) {
      out.fields = next
      break
    }

    cursor = next
  }

  return sanitizeClientDataFieldMap(out)
}

function changedDataEffectiveRoot(changedData: unknown): Record<string, any> | null {
  if (!changedData || typeof changedData !== 'object' || Array.isArray(changedData)) return null
  const o = changedData as Record<string, any>
  const inner = o.changed_data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return changedDataEffectiveRoot(inner) ?? (inner as Record<string, any>)
  }
  return o
}

/**
 * Плоский map полей из jsResult.changed_data
 */
export function resolveJsResultChangedDataFieldMap(changedData: unknown): Record<string, any> {
  if (changedData == null) return {}
  const root =
    changedDataEffectiveRoot(changedData) ??
    (typeof changedData === 'object' && !Array.isArray(changedData)
      ? (changedData as Record<string, any>)
      : null)
  if (!root) return {}
  const nested = root.fields?.fields
  const nestedObj =
    nested && typeof nested === 'object' && !Array.isArray(nested) ? (nested as Record<string, any>) : {}
  const walked = resolveClientDataActualFields({ fields: root })
  return sanitizeClientDataFieldMap(mergePageFieldMaps(nestedObj, walked))
}

/** Единый снимок полей страницы/диалога */
export function mergePageDataFieldSnapshot(pageData: any): Record<string, any> {
  const chain = pageData?.currentChainData?.fields?.fields
  const changed = resolveJsResultChangedDataFieldMap(pageData?.jsResult?.changed_data)
  const initial = pageData?.uniData?.fields?.fields
  // Единый источник истины для UI: jsResult.changed_data важнее устаревшего снимка uniData.
  // Слой changed_data идёт последним, чтобы свежие executeScript/action не затирались uniData.

  const merged = mergePageFieldMaps(initial, chain)
  return sanitizeClientDataFieldMap(overlayAuthoritativeFieldKeys(merged, changed))
}
